"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from "react";
import Link from "next/link";
import { SaleBannerCountdown } from "./SaleBannerCountdown";
function countryCodeToFlag(countryCode: string): string {
  if (!countryCode || countryCode === "XX" || countryCode.length !== 2) {
    return "";
  }
  const codePoints = countryCode.toUpperCase().split("").map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

// Truncate city names to prevent banner wrapping
// "New York City" → "New York", "San Francisco" → "San Francisco", "Philadelphia" → "Philadelph"
function truncateCity(city: string, maxLength = 12): string {
  if (!city || city.length <= maxLength) return city;
  const truncated = city.slice(0, maxLength);
  const lastSpace = truncated.lastIndexOf(" ");

  // Break at word boundary if reasonable (e.g., "New York City" → "New York")
  if (lastSpace > 5) return truncated.slice(0, lastSpace);

  // Hard truncate for single long words
  return truncated.trim();
}
interface GeoData {
  city: string;
  country: string;
}

// Robustly parse geo cookie, handling single or double URL-encoding
function parseGeoCookie(raw: string): GeoData | null {
  let value = raw;
  // Try up to 3 times: raw, single-decoded, double-decoded
  for (let i = 0; i < 3; i++) {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed.city === "string" && typeof parsed.country === "string") {
        return parsed as GeoData;
      }
      return null;
    } catch {
      // Not valid JSON, try decoding
      try {
        value = decodeURIComponent(value);
      } catch {
        return null; // Can't decode further
      }
    }
  }
  return null;
}

// Shimmer skeleton for location text
function LocationSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="relative mx-1 inline-block h-[1.1em] w-[70px] overflow-hidden rounded bg-white/20 align-middle"><span className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/40 to-transparent" /></span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function SaleBanner() {
  const $ = _c(16);
  const [geo, setGeo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const match = document.cookie.match(/(?:^|;\s*)geo=([^;]*)/);
      if (match) {
        const data = parseGeoCookie(match[1]);
        if (data) {
          setGeo(data);
        } else {
          setGeo({
            city: "",
            country: ""
          });
        }
      } else {
        setGeo({
          city: "",
          country: ""
        });
      }
      setIsLoading(false);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let locationText = "";
  if (geo?.city) {
    let t2;
    if ($[2] !== geo.country) {
      t2 = countryCodeToFlag(geo.country);
      $[2] = geo.country;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    const flag = t2;
    let t3;
    if ($[4] !== geo.city) {
      t3 = truncateCity(geo.city);
      $[4] = geo.city;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    const truncatedCity = t3;
    locationText = flag ? `${flag} ${truncatedCity}` : truncatedCity;
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      background: "linear-gradient(90deg, rgb(62,134,198) 0%, rgb(166,102,170) 25%, rgb(236,68,146) 50%, rgb(238,68,84) 75%, rgb(240,84,39) 100%)"
    };
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      color: "rgb(251, 251, 251)",
      fontSize: "clamp(14px, 3.8vw, 16px)"
    };
    t4 = <span className="whitespace-nowrap font-bold">70% Off Today</span>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[9] !== isLoading || $[10] !== locationText) {
    t5 = isLoading ? <LocationSkeleton /> : locationText ? <span className="animate-fade-in-gentle font-bold">{locationText}</span> : null;
    $[9] = isLoading;
    $[10] = locationText;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="mx-1.5 sm:mx-2">|</span>;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="whitespace-nowrap"><SaleBannerCountdown /></span>;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t5) {
    t8 = <Link href="/pricing" className="fixed top-0 left-0 right-0 z-[9999999] flex min-h-12 w-full items-center justify-center px-3 py-2.5 sm:px-4 sm:py-3" style={t2}><p className="text-center font-medium leading-tight" style={t3}>{t4}{" "}{t5}{t6}{t7}</p></Link>;
    $[14] = t5;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}